import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { WebComponentsLoaderService } from '@shared/webcomponents/webcomponents-loader/webcomponents-loader.service';
import { distinctUntilChanged, filter, startWith } from 'rxjs';
import { environment } from '@env/environment';
import { AppService, AuthService } from '@shared/services';
import { nativeFetch } from '@shared/services/rocos-sdk-client/native-fetch';

export interface EnvironmentConfig {
  mapBoxToken: string;
  googleMapsApiKey: string;
  tilesUrl: string;
  origin: { lat: number; lon: number; alt?: number };
}

export type RobotMapConfig = EnvironmentConfig &
  (
    | { mode: 'fixed'; mapId: string; parentFrame?: string; editMode?: boolean }
    | { mode: 'static'; mapHash: string; parentFrame?: string }
    | { mode: 'dynamic'; source: string; parentFrame?: string; editMode?: boolean }
  );

export interface OmniviewerResult {
  sceneJSON: string;
}

@Component({
  selector: 'app-cw-rv-environment',
  template: `<rc-cw-rv-environment
    [attr.projectid]="projectId"
    [attr.sdk]="{
        token: token$ | async,
        url: url,
        transport: transport,
      } | json"
    [attr.progressaiurl]="progressAIUrl"
    [attr.callsign]="'unknown'"
    [attr.config]="config | json"
    [sdkFetch]="sdkFetch"
    (scene)="onScene($event)"
  ></rc-cw-rv-environment>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CWRvEnvironmentComponent {
  @Input() config: EnvironmentConfig;
  @Output() public omniviewer: EventEmitter<OmniviewerResult> = new EventEmitter();

  token$ = this.authService.token$.pipe(filter(Boolean), distinctUntilChanged());

  url = new URL(environment.api.url).hostname;
  projectId = this.appService.projectId || '';
  // Pre-RUM fetch injected as a DOM property so the widget SDK reads it for both transports
  // (functions can't ride the serialized `sdk` attribute). See native-fetch.ts.
  protected readonly sdkFetch = nativeFetch;
  // Telemetry transport (grpc-web on dev/stage; SDK default grpc-web when unset).
  protected readonly transport = environment.api.transport;
  protected readonly progressAIUrl = environment.droneDeploy?.progressAIApi;

  constructor(
    private readonly webComponentLoader: WebComponentsLoaderService,
    public appService: AppService,
    private readonly authService: AuthService,
  ) {
    this.webComponentLoader.loadDefaultComponents();
  }

  public onScene(event: any) {
    event.stopPropagation();
    this.omniviewer.emit(event.detail as OmniviewerResult);
  }
}

/** Wrapper for the robot map viewer web component (rc-rv-robot-map). */
@Component({
  selector: 'app-cw-rv-robot-map',
  template: `<rc-rv-robot-map
    [attr.projectid]="projectId"
    [attr.callsign]="callsign$ | async"
    [attr.sdk]="{
        token: token$ | async,
        url: url,
        transport: transport,
      } | json"
    [attr.progressaiurl]="progressAIUrl"
    [attr.config]="config | json"
    [sdkFetch]="sdkFetch"
  ></rc-rv-robot-map>`,
  styles: [
    `
      :host {
        display: flex;
        flex: 1;
        min-height: 0;
        min-width: 0;
      }
      rc-rv-robot-map {
        display: block;
        flex: 1;
        min-height: 0;
        width: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CWRvRobotMapComponent {
  @Input() config: RobotMapConfig;

  token$ = this.authService.token$.pipe(filter(Boolean), distinctUntilChanged());
  callsign$ = this.appService.callsignChange.pipe(startWith(this.appService.callsign || ''), distinctUntilChanged());

  url = new URL(environment.api.url).hostname;
  projectId = this.appService.projectId || '';
  // Pre-RUM fetch injected as a DOM property so the widget SDK reads it for both transports
  // (functions can't ride the serialized `sdk` attribute). See native-fetch.ts.
  protected readonly sdkFetch = nativeFetch;
  // Telemetry transport (grpc-web on dev/stage; SDK default grpc-web when unset).
  protected readonly transport = environment.api.transport;
  protected readonly progressAIUrl = environment.droneDeploy?.progressAIApi;

  constructor(
    private readonly webComponentLoader: WebComponentsLoaderService,
    public appService: AppService,
    private readonly authService: AuthService,
  ) {
    this.webComponentLoader.loadDefaultComponents();
  }
}
